import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf, NgForOf, NgClass } from '@angular/common';
import { CreateGolfEventCourseSlot, CreateGolfEventRequest } from '../../../models/creategolfeventrequest';
import { GolfEventService } from '../../../services/golf-event.service';
import { PlayerService } from '../../../services/player.service';
import { Player } from '../../../models/Player';
import { SelectablePlayer } from '../../../models/viewmodels/selectableplayer';
import { ApplicationService } from '../../../services/application.service';
import { toCalendarControlFriendlyDate } from '../../../util/dateutil';
import { EVENT_FORMAT } from '../../../models/constants';

@Component({
  selector: 'app-create-event',
  standalone: true,
  imports: [FormsModule, NgIf, NgForOf, NgClass],
  templateUrl: './create-event.component.html',
  styleUrl: './create-event.component.css'
})
export class CreateEventComponent implements OnInit {

  constructor(private golfEventService:GolfEventService, private playerService:PlayerService, private applicationService:ApplicationService) {}

  @Output() saved = new EventEmitter<number>();
  @Output() canceled = new EventEmitter<void>();

  startDate:string = this.getNextBestEventStartDate();

  createGolfEventRequest:CreateGolfEventRequest = {
    description: '',
    endDate: this.calculateNextBestEventEndDate(this.startDate),
    entryFee: 0,
    eventName: '',
    firstPlacePrizeMoney: 0,
    secondPlacePrizeMoney: 0,
    startDate: this.startDate,
    thirdPlacePrizeMoney: 0,
    type: EVENT_FORMAT.MULTI_COURSE,
    playerIds: [],
    isTeamEvent: false,
    scoringType: 'SCORETOPAR',
    scoreAggregation: 'SUM',
    useHandicaps: true,
    courseSlots: []
  }

  currentStep:string = 'TYPESELECT'
  field:Array<SelectablePlayer> = [];

  formatOptions = [
    {
      type: EVENT_FORMAT.SINGLE_COURSE,
      name: 'Single Course Tournament',
      description: 'Played on one golf course over the event dates. Configure how many rounds count. Weeks do not apply.',
      hidden: false
    },
    {
      type: EVENT_FORMAT.MULTI_COURSE,
      name: 'Multi-Course Tournament',
      description: 'Played across multiple courses over the same event dates. Best X rounds count per course. Weeks do not apply.',
      hidden: false
    },
    {
      type: EVENT_FORMAT.HYBRID_SCRAMBLE,
      name: 'Hybrid Scramble',
      description: 'Every round is a 2 man scramble with any partner you like, but standings are individual. Best X net rounds per course are averaged, then course scores are added up.',
      hidden: false
    },
    {
      // Kept in code for existing leagues; hidden from create UI for now.
      type: EVENT_FORMAT.MULTI_WEEK_LEAGUE,
      name: 'Multi-Week League',
      description: 'Courses are assigned to specific weeks (Week 1 at Course A, Week 2 at Course B, and so on). Best X rounds count per week.',
      hidden: true
    }
  ];

  get visibleFormatOptions() {
    return this.formatOptions.filter(option => !option.hidden);
  }

  ngOnInit(): void {
   this.getPlayers(); 
  }

  get formatNextLabel():string {
    return this.needsCourseSetup
      ? 'Next: Set Course(s) >'
      : 'Next: Set the Field >';
  }

  get needsCourseSetup():boolean {
    return this.createGolfEventRequest.type === EVENT_FORMAT.SINGLE_COURSE
      || this.createGolfEventRequest.type === EVENT_FORMAT.MULTI_COURSE
      || this.createGolfEventRequest.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get isHybridScramble():boolean {
    return this.createGolfEventRequest.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get allowsMultipleCourses():boolean {
    return this.createGolfEventRequest.type === EVENT_FORMAT.MULTI_COURSE
      || this.createGolfEventRequest.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  moveToStep(stepName:string):void {
    this.currentStep = stepName;
  }

  goToNextFromFormat():void {
    this.onFormatTypeChange(this.createGolfEventRequest.type);
    this.moveToStep(this.needsCourseSetup ? 'COURSES' : 'PLAYERSELECT');
  }

  backFromField():void {
    this.moveToStep(this.needsCourseSetup ? 'COURSES' : 'FORMAT');
  }

  selectEventType(formatType:string):void {
    this.onFormatTypeChange(formatType);
    this.moveToStep('EVENTINFO');
  }

  onFormatTypeChange(formatType:string):void {
    this.createGolfEventRequest.type = formatType;
    if (formatType === EVENT_FORMAT.SINGLE_COURSE) {
      if (this.createGolfEventRequest.courseSlots.length !== 1) {
        this.createGolfEventRequest.courseSlots = [this.emptyCourseSlot()];
      }
    } else if (this.allowsMultipleCourses) {
      if (this.createGolfEventRequest.courseSlots.length === 0) {
        this.createGolfEventRequest.courseSlots = [this.emptyCourseSlot()];
      }
    } else {
      this.createGolfEventRequest.courseSlots = [];
    }

    // Hybrid scramble scoring is fixed: individual standings, net scores, best X nets averaged per course.
    if (this.isHybridScramble) {
      this.createGolfEventRequest.isTeamEvent = false;
      this.createGolfEventRequest.useHandicaps = true;
      this.createGolfEventRequest.scoreAggregation = 'AVG';
      this.createGolfEventRequest.courseSlots.forEach(slot => { slot.roundsToCount = 3; });
    }
  }

  emptyCourseSlot():CreateGolfEventCourseSlot {
    return {
      golfCourseName: '',
      teeLocation: '',
      pinLocations: 'Thursday',
      gimmeCircleSize: 12,
      holesToPlay: 'FRONT9',
      roundsToCount: this.isHybridScramble ? 3 : 1,
      maxRoundsToPost: 99,
      coursePar: 36
    };
  }

  addCourseSlot():void {
    this.createGolfEventRequest.courseSlots.push(this.emptyCourseSlot());
  }

  removeCourseSlot(index:number):void {
    this.createGolfEventRequest.courseSlots.splice(index, 1);
  }

  async getPlayers():Promise<void> {
    let players:Array<Player> = await this.playerService.searchPlayers(null);
    players.forEach(p => {
      this.field.push({ isSelected:false, player: p });
    });
  }

  getFinalField():Array<SelectablePlayer> {
    return this.field.filter(f => f.isSelected);
  }

  async save() {
    let includedPlayers = this.getFinalField();
    this.createGolfEventRequest.playerIds = includedPlayers.map(p => p.player.id);
    this.createGolfEventRequest.scoringType = 'SCORETOPAR';
    if (this.isHybridScramble) {
      this.createGolfEventRequest.isTeamEvent = false;
      this.createGolfEventRequest.useHandicaps = true;
      this.createGolfEventRequest.scoreAggregation = 'AVG';
    }
    if (!this.needsCourseSetup) {
      this.createGolfEventRequest.courseSlots = [];
    } else {
      this.createGolfEventRequest.courseSlots = this.createGolfEventRequest.courseSlots.map(slot => ({
        ...slot,
        gimmeCircleSize: Number(slot.gimmeCircleSize),
        roundsToCount: Number(slot.roundsToCount),
        maxRoundsToPost: Number(slot.maxRoundsToPost),
        coursePar: Number(slot.coursePar)
      }));
    }
    this.applicationService.showGlobalSpinner();
    let eventId = await this.golfEventService.createEvent(this.createGolfEventRequest);
    this.applicationService.hideGlobalSpinner();
    this.saved.emit(eventId);
  }

  getNextBestEventStartDate():string {
    let dte = new Date();
    return toCalendarControlFriendlyDate(dte);
  }

  calculateNextBestEventEndDate(startDate:string) {
    let start = new Date(startDate);
    let leagueLength = 30;
    let endDate = new Date(start);
    endDate.setDate(endDate.getDate() + leagueLength);
    return toCalendarControlFriendlyDate(endDate);
  }

  cancel() {
    this.canceled.emit();
  }
}
